import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Chart } from 'react-google-charts'
import Spinner from '../../../util/spinner-shim'
import { chartDimensions } from 'altrac-conversions'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/charts.module.css'

const Cell = function Cell ({ value }) {
  return (
    <td>
      {value}
    </td>
  )
}

Cell.propTypes = {
  value: PropTypes.node,
}

const Row = function Row ({ data }) {
  const cells = []
  for (let i = 0; i < data.length; i++) {
    let cellValue
    if (typeof data[i].getMonth === 'function') {
      cellValue = data[i].toLocaleString()
    } else {
      cellValue = data[i]
    }
    cells.push(
      <Cell key={i} value={cellValue} />
    )
  }
  return (
    <tr>
      {cells}
    </tr>
  )
}

Row.propTypes = {
  data: PropTypes.array,
}

const Table = function Table ({ rows, columns }) {
  if (columns.length < 1) {
    // No column schema yet — still initialising; the outer component
    // owns the loading state, so don't double up with a spinner here.
    return null
  }
  const columnArr = []
  for (let i = 0; i < columns.length; i++) {
    columnArr.push(columns[i].label)
  }
  if (rows.length < 1) {
    // Loaded with no data for the period — render the header only so the
    // user sees an empty table instead of an infinite spinner.
    return (
      <table className='table table-striped'>
        <thead>
          <Row data={columnArr} />
        </thead>
        <tbody />
      </table>
    )
  }
  const rowsArr = []
  for (let i = 0; i < rows.length; i++) {
    rowsArr.push(
      <Row key={i} data={rows[i]} />
    )
  }
  return (
    <table className='table table-striped'>
      <thead>
        <Row data={columnArr} />
      </thead>
      <tbody>
        {rowsArr}
      </tbody>
    </table>
  )
}

Table.propTypes = {
  rows: PropTypes.array,
  columns: PropTypes.array,
}

function TelemetryET ({
  device, periodStart, periodStop, sensorData,
}) {
  const [rows, setRows] = useState([])
  const [columns, setColumns] = useState([])
  const [options, setOptions] = useState({})

  const getNewData = useCallback((address, periodStartParam, periodStopParam) => {
    AppDispatcher.dispatch({
      action: 'get-metric-address',
      address: 'KORROSEB11;0.15',
      timeBeg: periodStartParam || periodStart,
      timeEnd: periodStopParam || periodStop,
    })
  }, [periodStart, periodStop])

  const fillChart = useCallback((sensorData) => {
    const options = {
      title: 'ET Review',
      hAxis: {
        gridlines: {
          count: -1,
          units: {
            days: { format: ['M/d'] },
            hours: { format: ['M/d ha'] },
          },
        },
        minorGridlines: {
          units: {
            hours: { format: ['hh:mm:ss a', 'ha'] },
            minutes: { format: ['HH:mm a Z', ':mm'] },
          },
        },
      },
      vAxis: {
        title: 'On/Off',
        minValue: 0,
        maxValue: 1,
      },
      chartArea: {
        left: 0,
        top: 0,
        width: '100%',
        height: '85%',
      },
      isStacked: true,
      legend: { position: 'bottom' },
    }
    const rows = []

    if (
      sensorData.length > 0
    ) {
      for (let i = 0; i < sensorData.length; i++) {
        rows.push(
          [
            new Date(sensorData[i].date),
            sensorData[i].response.evapotranspirationIN,
          ]
        )
      }
    }

    const columns = [
      {
        type: 'date',
        label: 'Time',
      },
      {
        type: 'number',
        label: 'ET',
      },
    ]

    setRows(rows)
    setColumns(columns)
    setOptions(options)
  }, [])

  useEffect(() => {
    getNewData()
  }, [getNewData])

  useEffect(() => {
    const interval = setInterval(getNewData, 20000)
    return () => {
      clearInterval(interval)
    }
  }, [getNewData])

  useEffect(() => {
    console.log(sensorData)
    if (periodStart !== undefined && periodStop !== undefined) {
      getNewData(null, periodStart, periodStop)
    }
    fillChart(sensorData)
  }, [periodStart, periodStop, sensorData, getNewData, fillChart])

  if (
    typeof rows !== 'object' ||
    typeof columns !== 'object' ||
    rows.length < 1 &&
    columns.length < 1
  ) {
    return (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  }
  const dimensions = chartDimensions(window.innerWidth)
  return (
    <div className='deviceInfo'>
      <div className={styles.chart}>
        <Chart
          chartType='AreaChart'
          rows={rows}
          width={dimensions.width}
          height={dimensions.height}
          columns={columns}
          options={options}
          style={{
            margin: '0 auto',
            position: 'relative',
            top: '5px',
          }}
        />
      </div>
      <Table
        rows={rows}
        columns={columns}
      />
    </div>
  )
}

TelemetryET.propTypes = {
  device: PropTypes.object,
  periodStart: PropTypes.string,
  periodStop: PropTypes.string,
  sensorData: PropTypes.object,
}

export default TelemetryET
